/**
 * Topic index pages: one cross-brand ranked table per spec type.
 *
 * These sit between the site root and the thousands of model-level leaf pages.
 * Their job is structural: they collect link equity for a whole content type
 * and give every leaf page a topical parent, so a `/ford/f150/towing-capacity`
 * page is part of a cluster instead of standing alone.
 *
 * A topic names the table it reads from, the column it ranks on, and any extra
 * columns worth showing next to the ranked value.
 */

export type TopicPath =
  | "/towing-capacity"
  | "/truck-bed-dimensions"
  | "/fuel-tank-capacity"
  | "/mpg-comparison"
  | "/car-dimensions"
  | "/car-safety-ratings";

/** Table a topic reads its ranked value from. */
export type TopicSource =
  | "vehicle_specs"
  | "vehicles"
  | "vehicle_dimensions"
  | "safety_ratings";

export interface TopicColumn {
  /** Key inside TopicEntry.extra. */
  key: string;
  label: string;
  decimals: number;
  /** Hide below this breakpoint to keep mobile tables readable. */
  hideBelow?: "sm" | "md" | "lg";
}

export interface TopicDef {
  /** URL slug — the page lives at /{slug}. */
  slug: string;
  /** Router path literal, so links stay type-checked. */
  path: TopicPath;
  /** Table the ranked value comes from. */
  source: TopicSource;
  /** Leaf data type this topic is the parent of (also vehicle_specs.spec_type). */
  specType: string;
  /** Column holding the ranked value. */
  column: string;
  /** Extra numeric columns rendered beside the ranked value. */
  extraColumns?: TopicColumn[];
  h1: string;
  title: string;
  description: string;
  metricLabel: string;
  unit: string;
  /** Suffix used when writing the value into prose. */
  unitLong: string;
  intro: string;
  notes: string;
  /** How the value is formatted in the table. */
  decimals: number;
}

export const TOPICS: TopicDef[] = [
  {
    slug: "towing-capacity",
    path: "/towing-capacity",
    source: "vehicle_specs",
    specType: "towing-capacity",
    column: "towing_capacity_lbs",
    h1: "Towing Capacity by Vehicle",
    title: "Towing Capacity Chart — Every Truck and SUV",
    description:
      "Maximum towing capacity for every truck and SUV in our database, ranked, with the exact configuration each rating applies to.",
    metricLabel: "Max towing",
    unit: "lbs",
    unitLong: "pounds",
    intro:
      "Every figure below is a manufacturer-published maximum for a properly equipped configuration — not an average, and not what your specific truck will tow off the lot. Options, cab style, axle ratio and bed length all move the number. Click any vehicle for the full year-by-year breakdown and the 80% safe working load.",
    notes:
      "A max rating assumes an empty truck with a 150 lb driver. Subtract passengers, cargo and tongue weight before you compare it to your trailer. Plan around 80% of the published figure for anything you tow regularly.",
    decimals: 0,
  },
  {
    slug: "truck-bed-dimensions",
    path: "/truck-bed-dimensions",
    source: "vehicle_specs",
    specType: "bed-dimensions",
    column: "bed_length_in",
    h1: "Truck Bed Dimensions by Model",
    title: "Truck Bed Size Chart — Length, Width and Volume",
    description:
      "Truck bed length, width and cargo volume for every pickup in our database, ranked by bed length, with each configuration listed separately.",
    metricLabel: "Longest bed",
    unit: "in",
    unitLong: "inches",
    intro:
      "Bed length is measured at the floor, inside the bulkhead to the closed tailgate. What actually matters for sheet goods is the width between the wheel wells — anything under 49.5 inches means 4x8 plywood rides on top of the wells, not flat on the floor. Each model links to every bed configuration it offers.",
    notes:
      "Manufacturers round bed lengths in marketing ('6-foot bed' is usually 67 to 78 inches depending on brand). The figures here are the measured floor lengths, which is why two trucks advertised the same can differ by half a foot.",
    decimals: 1,
  },
  {
    slug: "fuel-tank-capacity",
    path: "/fuel-tank-capacity",
    source: "vehicle_specs",
    specType: "fuel-tank-capacity",
    column: "fuel_tank_gal",
    h1: "Fuel Tank Capacity by Vehicle",
    title: "Gas Tank Size Chart — Fuel Capacity by Model",
    description:
      "Fuel tank capacity in gallons for every vehicle in our database, ranked, with optional extended tanks noted where the manufacturer offers them.",
    metricLabel: "Tank size",
    unit: "gal",
    unitLong: "gallons",
    intro:
      "Tank capacity is the usable volume the manufacturer publishes. Your low-fuel light typically comes on with 10 to 15 percent still in the tank, so real range between fill-ups is shorter than capacity multiplied by highway MPG. Each model links to its full tank and range data.",
    notes:
      "Some trucks and full-size SUVs offer an optional larger tank on specific trims. Where that exists, the model page lists both the standard and extended capacity.",
    decimals: 1,
  },
  {
    slug: "mpg-comparison",
    path: "/mpg-comparison",
    source: "vehicles",
    specType: "mpg",
    column: "mpg_combined",
    extraColumns: [
      { key: "city", label: "City", decimals: 0, hideBelow: "sm" },
      { key: "hwy", label: "Highway", decimals: 0, hideBelow: "sm" },
    ],
    h1: "MPG Comparison Across Every Brand",
    title: "MPG Comparison Chart — Combined, City and Highway",
    description:
      "EPA combined, city and highway MPG for every gas, diesel and hybrid model in our database, ranked best to worst in one table.",
    metricLabel: "Combined MPG",
    unit: "mpg",
    unitLong: "combined MPG",
    intro:
      "Each row shows the most efficient EPA-tested configuration a model offers, so a hybrid trim will represent a nameplate that also sells a V6. Combined MPG is a 55/45 city-highway blend, which is the number that actually predicts your fuel bill — highway-only figures flatter big engines and hurt hybrids. Battery-electric models are excluded because MPGe is a different measurement and mixing the two makes the ranking meaningless.",
    notes:
      "EPA figures come from a standardised dyno cycle, not from road testing. Real-world results run roughly 10 to 15 percent lower in cold weather and on short trips, and hybrids lose the most in sustained highway driving because the engine runs continuously. Compare vehicles inside the same class before comparing across classes.",
    decimals: 0,
  },
  {
    slug: "car-dimensions",
    path: "/car-dimensions",
    source: "vehicle_dimensions",
    specType: "dimensions",
    column: "length_in",
    extraColumns: [
      { key: "width", label: "Width (in)", decimals: 1, hideBelow: "sm" },
      { key: "height", label: "Height (in)", decimals: 1, hideBelow: "md" },
      { key: "wheelbase", label: "Wheelbase (in)", decimals: 1, hideBelow: "md" },
      { key: "curbWeight", label: "Curb weight (lbs)", decimals: 0, hideBelow: "lg" },
    ],
    h1: "Car Dimensions by Model",
    title: "Car Dimensions Chart — Length, Width, Height, Weight",
    description:
      "Exterior length, width, height, wheelbase and curb weight for every model in our database, ranked longest to shortest.",
    metricLabel: "Length",
    unit: "in",
    unitLong: "inches",
    intro:
      "Overall length is bumper to bumper, which is the figure that decides whether a vehicle fits a garage or a parallel parking space. Width here excludes mirrors — add roughly 8 to 10 inches for mirrors out when you are measuring a garage door or a car wash bay. Wheelbase matters more than length for ride quality and interior room: two vehicles the same length can differ by a foot of wheelbase and feel like different classes.",
    notes:
      "Figures come from NHTSA's Canadian Vehicle Specifications dataset, measured on a stock configuration. Off-road packages, roof rails, running boards and towing mirrors all add to the published numbers, and curb weight moves several hundred pounds between a base trim and a loaded one.",
    decimals: 1,
  },
  {
    slug: "car-safety-ratings",
    path: "/car-safety-ratings",
    source: "safety_ratings",
    specType: "safety-ratings",
    column: "overall_rating",
    extraColumns: [
      { key: "front", label: "Frontal", decimals: 0, hideBelow: "sm" },
      { key: "side", label: "Side", decimals: 0, hideBelow: "sm" },
      { key: "rollover", label: "Rollover", decimals: 0, hideBelow: "md" },
    ],
    h1: "NHTSA Safety Ratings by Model",
    title: "Car Safety Ratings Chart — NHTSA Stars by Model",
    description:
      "NHTSA overall, frontal, side and rollover star ratings for every model in our database, ranked, with recall and complaint counts on each model page.",
    metricLabel: "Overall stars",
    unit: "★",
    unitLong: "stars overall",
    intro:
      "NHTSA rates crashworthiness on a five-star scale across frontal impact, side impact and rollover resistance, then combines them into an overall score. A five-star overall does not mean five stars in every subtest, which is why the frontal, side and rollover columns are shown separately here — a vehicle can carry five stars overall while scoring four in rollover.",
    notes:
      "Star ratings only compare vehicles of similar weight. A five-star small car and a five-star full-size SUV are not equally safe in a collision with each other, because physics does not care about stars. Ratings also reset when a model is redesigned, so an unrated model year usually means the vehicle is new or too low-volume to have been tested, not that it failed.",
    decimals: 0,
  },
];

export const TOPIC_SLUGS = TOPICS.map((t) => t.slug);

export function getTopic(slug: string): TopicDef | undefined {
  return TOPICS.find((t) => t.slug === slug);
}

/** Topic index a leaf spec page should link up to, if one exists. */
export function topicForDataType(dataType: string): TopicDef | undefined {
  return TOPICS.find((t) => t.specType === dataType);
}
